<template>
  <div class="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900/50 min-h-screen overflow-x-hidden">
    <!-- Header con animación de entrada -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 sm:mb-8 animate-slideDown">
      <div class="w-full md:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 tracking-widest uppercase">Centro de Mensajes</span>
        </div>
        <h1 class="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex flex-wrap items-center gap-2 sm:gap-3">
          Notificaciones
          <span 
            v-if="noLeidas > 0" 
            class="px-2.5 py-1 bg-red-100 text-red-600 text-[10px] sm:text-xs font-black rounded-full border border-red-200 animate-pulseSoft"
          >
            {{ noLeidas }} pendientes
          </span>
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-[11px] sm:text-sm font-bold mt-1">Historial detallado de actividades y alertas críticas.</p>
      </div>

      <div class="flex gap-2 sm:gap-3 w-full md:w-auto">
        <button 
          @click="marcarTodasLeidas"
          class="flex-1 md:flex-none px-3 sm:px-5 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center gap-2 active:scale-95"
        >
          <Icon icon="mdi:check-all" class="text-base sm:text-lg" />
          <span class="hidden xs:inline">Marcar Todo</span>
          <span class="xs:hidden">Leídas</span>
        </button>
        <button 
          @click="limpiarHistorial"
          class="flex-1 md:flex-none px-3 sm:px-5 py-2.5 bg-rose-50 text-rose-600 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-rose-100 transition-all border border-rose-100 shadow-sm flex items-center justify-center gap-2 active:scale-95"
        >
          <Icon icon="mdi:trash-can-outline" class="text-base sm:text-lg" />
          <span class="hidden xs:inline">Limpiar</span>
          <span class="xs:hidden">Limpiar</span>
        </button>
      </div>
    </div>

    <!-- Filtros con scroll horizontal en móvil -->
    <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm mb-5 sm:mb-6 animate-slideUp">
      <div class="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 whitespace-nowrap flex-shrink-0">Filtrar:</span>
        <button 
          v-for="f in filtros" 
          :key="f.id"
          @click="filtroActivo = f.id"
          :class="[
            'px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-shrink-0 active:scale-95',
            filtroActivo === f.id 
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 scale-105' 
              : 'bg-gray-50 dark:bg-gray-900/50 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700'
          ]"
        >
          {{ f.nombre }}
        </button>
      </div>
    </div>

    <!-- Lista con animaciones escalonadas -->
    <div class="space-y-3 sm:space-y-4 max-w-4xl mx-auto">
      <!-- Estado vacío -->
      <Transition name="fade" mode="out-in">
        <div 
          v-if="notificacionesFiltradas.length === 0" 
          class="text-center py-16 sm:py-20 bg-white dark:bg-gray-800 rounded-3xl sm:rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-700 animate-scaleIn"
        >
          <Icon icon="mdi:bell-off-outline" class="text-6xl sm:text-7xl text-gray-200 dark:text-gray-700 mx-auto mb-4 animate-float" />
          <h3 class="text-lg sm:text-xl font-black text-gray-400 uppercase tracking-widest">No hay notificaciones</h3>
          <p class="text-gray-400 font-bold mt-2 text-sm">Tu historial está completamente limpio.</p>
        </div>
      </Transition>

      <!-- Tarjetas de notificación con animación escalonada -->
      <TransitionGroup name="list" tag="div" class="space-y-3 sm:space-y-4">
        <div 
          v-for="(notif, index) in notificacionesFiltradas" 
          :key="notif.id"
          class="group relative bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          :class="{ 'border-l-[4px] sm:border-l-[6px] border-l-blue-500': !notif.leida }"
          :style="{ animationDelay: `${index * 50}ms` }"
        >
          <div class="flex gap-3 sm:gap-6 items-start">
            <!-- Icono animado -->
            <div class="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gray-50 dark:bg-gray-900/50 flex items-center justify-center text-2xl sm:text-4xl shadow-inner group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 flex-shrink-0">
              {{ notif.icono || '🔔' }}
            </div>
            
            <div class="flex-1 min-w-0">
              <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-2 mb-1">
                <div class="min-w-0">
                  <span class="text-[9px] sm:text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest block mb-0.5">
                    {{ notif.modulo }}
                  </span>
                  <h3 class="text-base sm:text-lg font-black text-gray-900 dark:text-white leading-tight break-words">
                    {{ notif.titulo }}
                  </h3>
                </div>
                <span class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full self-start">
                  {{ tiempoRelativo(notif.fecha) }}
                </span>
              </div>
              
              <p class="text-gray-600 dark:text-gray-400 font-bold text-xs sm:text-sm mb-2 sm:mb-3 break-words">
                {{ notif.detalle }}
              </p>
              
              <div class="flex flex-wrap items-center gap-2 sm:gap-4">
                <span :class="[
                  'px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg text-[8px] sm:text-[9px] font-black uppercase tracking-widest transition-transform group-hover:scale-105',
                  notif.accion === 'Crítico' ? 'bg-red-100 text-red-600 animate-pulseSoft' : (notif.accion && notif.accion.includes('Verifi') ? 'bg-amber-100 text-amber-800 border border-amber-300 font-black' : 'bg-blue-100 text-blue-600')
                ]">
                  {{ notif.accion }}
                </span>
                <span class="text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">
                  {{ formatDateTime(notif.fecha) }}
                </span>
                <button
                  v-if="notif.modulo === 'pago' || notif.ruta === '/pagos'"
                  @click.stop="$router.push('/pagos')"
                  class="ml-auto px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1 shadow-sm active:scale-95"
                >
                  <Icon icon="mdi:credit-card-check" class="text-sm" />
                  Verificar en Pagos
                </button>
              </div>
            </div>

            <!-- Botones acción individual (marcar leída y borrar) -->
            <div class="flex items-center gap-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button 
                v-if="!notif.leida"
                @click.stop="marcarLeida(notif.id)"
                class="p-1.5 sm:p-2 bg-blue-50 text-blue-600 rounded-lg sm:rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-90"
                title="Marcar como leída"
              >
                <Icon icon="mdi:check" class="text-sm sm:text-base" />
              </button>
              <button 
                @click.stop="eliminarUnaNotificacion(notif)"
                class="p-1.5 sm:p-2 bg-rose-50 text-rose-600 rounded-lg sm:rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm active:scale-90"
                title="Borrar notificación"
              >
                <Icon icon="mdi:trash-can-outline" class="text-sm sm:text-base" />
              </button>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- Modal de Confirmación -->
    <ModalConfirm 
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      :icon="confirmModal.icon"
      :confirmText="confirmModal.confirmText"
      @confirm="ejecutarAccionConfirmada"
      @cancel="confirmModal.show = false"
    />
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { 
  cargarNotificaciones, 
  marcarTodasLeidas, 
  marcarLeida, 
  eliminarNotificacion,
  limpiarNotificaciones,
  tiempoRelativo 
} from "@/utils/notificaciones";
import { useToast } from "vue-toastification";
import ModalConfirm from "@/components/ModalConfirm.vue";

export default {
  name: "NotificacionesList",
  components: { Icon, ModalConfirm },
  data() {
    return {
      notificaciones: [],
      filtroActivo: 'todas',
      filtros: [
        { id: 'todas', nombre: 'Todas' },
        { id: 'pendientes', nombre: 'Pendientes' },
        { id: 'pago', nombre: 'Pagos Online' },
        { id: 'critico', nombre: 'Críticas' },
        { id: 'credito', nombre: 'Finanzas' }
      ],
      confirmModal: {
        show: false,
        title: '',
        message: '',
        type: 'primary',
        icon: '',
        confirmText: '',
        callback: null
      }
    };
  },
  computed: {
    noLeidas() {
      return this.notificaciones.filter(n => !n.leida).length;
    },
    notificacionesFiltradas() {
      let list = [...this.notificaciones];
      if (this.filtroActivo === 'pendientes') list = list.filter(n => !n.leida);
      if (this.filtroActivo === 'pago') list = list.filter(n => n.modulo === 'pago');
      if (this.filtroActivo === 'critico') list = list.filter(n => n.accion === 'Crítico');
      if (this.filtroActivo === 'credito') list = list.filter(n => n.modulo === 'credito');
      return list;
    }
  },
  methods: {
    tiempoRelativo,
    cargar() {
      this.notificaciones = cargarNotificaciones();
    },
    marcarTodasLeidas() {
      marcarTodasLeidas();
      this.cargar();
    },
    marcarLeida(id) {
      marcarLeida(id);
      this.cargar();
    },
    eliminarUnaNotificacion(notif) {
      eliminarNotificacion(notif.id);
      this.cargar();
    },
    limpiarHistorial() {
      this.confirmModal = {
        show: true,
        title: 'Limpiar Historial',
        message: '¿Estás seguro de eliminar todo el historial de notificaciones? <br><br> Esta acción no se puede deshacer.',
        type: 'danger',
        icon: 'mdi:trash-can-alert',
        confirmText: 'Sí, limpiar todo',
        callback: () => {
          limpiarNotificaciones();
          this.cargar();
        }
      };
    },
    ejecutarAccionConfirmada() {
      this.confirmModal.show = false;
      if (this.confirmModal.callback) {
        this.confirmModal.callback();
      }
    },
    formatDateTime(iso) {
      const d = new Date(iso);
      return d.toLocaleString('es-VE', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  mounted() {
    this.cargar();
    window.addEventListener('notificacionNueva', this.cargar);
    window.addEventListener('notificacionesActualizadas', this.cargar);
  },
  beforeUnmount() {
    window.removeEventListener('notificacionNueva', this.cargar);
    window.removeEventListener('notificacionesActualizadas', this.cargar);
  }
};
</script>

<style scoped>
/* Ocultar scrollbar en filtros móviles */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Animaciones de entrada */
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-slideDown {
  animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.animate-slideUp {
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.1s;
  opacity: 0;
}
.animate-scaleIn {
  animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.animate-float {
  animation: float 3s ease-in-out infinite;
}
.animate-pulseSoft {
  animation: pulseSoft 2s ease-in-out infinite;
}

/* Transiciones para la lista */
.list-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.list-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: absolute;
}
.list-enter-from {
  opacity: 0;
  transform: translateX(-20px) scale(0.95);
}
.list-leave-to {
  opacity: 0;
  transform: translateX(20px) scale(0.95);
}
.list-move {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Transición fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Breakpoint personalizado para xs */
@media (min-width: 400px) {
  .xs\:inline { display: inline; }
  .xs\:hidden { display: none; }
}
@media (max-width: 399px) {
  .xs\:inline { display: none; }
  .xs\:hidden { display: inline; }
}
</style>
